/*
  ========================================================================
  THEME: Impact v7.0.1 merge target + Gro unified rebuild
  STORES: Gro Clinics AU (groclinics.com.au) + NZ (groclinics.co.nz)
  LAST UPDATED: 04/08/2026
  ========================================================================
  gro-article.js - progressive enhancement for Gro article presentation.

  OWNERSHIP
    Owns adjacency image grouping, reading time, TOC, scroll state, image
    fallback and native sharing. References, disclaimer and article body remain
    server-rendered Liquid.

  GRO MODULE CONTRACT - read before editing
    Image groups ARE inferred from adjacency: a run of two or more consecutive
    image-only p/figure blocks becomes one grid. Empty editor filler blocks
    between them do not break the run and are removed when a group forms.
    Images inside gro-video are excluded.

    COMPLIANCE: adjacency grouping places images side by side without an editor
    asking for it, so two clinical photos can read as an implied before/after -
    the exact juxtaposition AHPRA's higher-risk cosmetic advertising guidelines
    restrict. This behaviour was requested deliberately on 04/08/2026, replacing
    the previous opt-in gro-article-image--group marker. The escape hatch is
    gro-article-image--solo on the p/figure or the image, which forces that
    image to stand alone and breaks the run. Put it on any clinical photograph
    that must not be paired.

    Initialisation must be idempotent and support Shopify theme-editor section
    loads. Modern vanilla JavaScript only.

  DEPENDENCIES
    Stable data-gro-* hooks emitted by Gro article snippets.

  DELIBERATE EXEMPTIONS
    None.
  ========================================================================
*/

(() => {
  if (window.groArticleModuleLoaded) return;
  window.groArticleModuleLoaded = true;

  const SOLO_CLASS = 'gro-article-image--solo';
  const INITIALISED = 'groArticleInitialised';

  const isImageOnlyBlock = (element) => {
    if (!element.matches('p, figure')) return false;
    if (element.closest('.gro-video')) return false;

    const images = [...element.querySelectorAll('img')].filter(
      (image) => !image.closest('.gro-video')
    );

    if (images.length !== 1) return false;
    if (element.classList.contains(SOLO_CLASS) || images[0].classList.contains(SOLO_CLASS)) {
      return false;
    }

    return element.textContent.trim() === '';
  };

  // Shopify's editor scatters empty <p> and <p>&nbsp;</p> between blocks. They
  // carry no content and must not break an otherwise adjacent run.
  const isEmptyFiller = (element) => {
    if (element.querySelector('img, iframe, video')) return false;
    return element.textContent.trim() === '';
  };

  const groupAdjacentImages = (content) => {
    const children = [...content.children];
    let run = [];
    let pendingFillers = [];
    let absorbedFillers = [];

    const reset = () => {
      run = [];
      pendingFillers = [];
      absorbedFillers = [];
    };

    const flush = () => {
      if (run.length < 2) {
        reset();
        return;
      }

      absorbedFillers.forEach((filler) => filler.remove());

      const wrapper = document.createElement('div');
      wrapper.className = 'gro-article-image-grid';
      wrapper.dataset.groArticleImageGrid = '';
      wrapper.style.setProperty(
        '--gro-article-image-columns',
        String(run.length === 4 ? 2 : Math.min(run.length, 3))
      );

      run[0].before(wrapper);
      run.forEach((item) => wrapper.append(item));
      reset();
    };

    children.forEach((element) => {
      if (isImageOnlyBlock(element)) {
        // Fillers only count as absorbed once they sit between two images.
        if (run.length > 0) absorbedFillers.push(...pendingFillers);
        pendingFillers = [];
        run.push(element);
        return;
      }

      if (run.length > 0 && isEmptyFiller(element)) {
        pendingFillers.push(element);
        return;
      }

      flush();
    });

    flush();
  };

  const initialiseFallbackImages = (scope) => {
    const fallback = scope.dataset.groDefaultImage;
    if (!fallback) return;

    scope.querySelectorAll('img').forEach((image) => {
      if (image.dataset.groFallbackBound === 'true') return;
      image.dataset.groFallbackBound = 'true';
      image.addEventListener('error', () => {
        if (image.src === fallback) return;
        image.src = fallback;
      });
    });
  };

  const slugify = (value) => value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

  const initialiseArticleContent = (content) => {
    const articleSection = content.closest('[data-gro-article-root]');
    if (!articleSection || content.dataset[INITIALISED] === 'true') return;
    content.dataset[INITIALISED] = 'true';

    groupAdjacentImages(content);
    initialiseFallbackImages(articleSection);

    const words = content.textContent.trim().split(/\s+/).filter(Boolean).length;
    const readingTime = `${Math.max(1, Math.ceil(words / 300))} min read`;
    document.querySelectorAll('[data-gro-reading-time]').forEach((element) => {
      element.textContent = readingTime;
    });

    const toc = articleSection.querySelector('[data-gro-toc]');
    if (!toc) return;

    toc.replaceChildren();
    const headings = [...content.querySelectorAll('h2')];
    const controller = new AbortController();
    articleSection.groArticleController?.abort();
    articleSection.groArticleController = controller;

    const setActive = (href) => {
      toc.querySelectorAll('a').forEach((link) => {
        link.classList.toggle('is-active', link.getAttribute('href') === href);
      });
    };

    headings.forEach((heading, index) => {
      if (!heading.id) {
        heading.id = `article-${slugify(heading.textContent) || 'section'}-${index + 1}`;
      }

      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActive(link.getAttribute('href'));
      });

      toc.appendChild(document.createElement('li')).appendChild(link);
    });

    const references = articleSection.querySelector('#references-section');
    if (references) {
      const link = document.createElement('a');
      link.href = '#references-section';
      link.textContent = 'References';
      link.addEventListener('click', (event) => {
        event.preventDefault();
        references.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActive('#references-section');
      });
      toc.appendChild(document.createElement('li')).appendChild(link);
    }

    window.addEventListener('scroll', () => {
      const active = headings.findLast(
        (heading) => window.scrollY >= heading.getBoundingClientRect().top + window.scrollY - 140
      );
      if (active) setActive(`#${active.id}`);
    }, { passive: true, signal: controller.signal });
  };

  const initialiseShareButtons = (scope) => {
    scope.querySelectorAll('[data-gro-share]').forEach((button) => {
      if (button.dataset.groShareBound === 'true') return;
      button.dataset.groShareBound = 'true';

      button.addEventListener('click', async () => {
        const shareData = { title: document.title, url: window.location.href };

        if (navigator.share) {
          try {
            await navigator.share(shareData);
          } catch (error) {
            if (error.name !== 'AbortError') console.warn('Article share failed', error);
          }
          return;
        }

        try {
          await navigator.clipboard.writeText(shareData.url);
          button.textContent = 'Link copied';
        } catch (error) {
          console.warn('Article link copy failed', error);
        }
      });
    });
  };

  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-gro-article-content]').forEach(initialiseArticleContent);
    scope.querySelectorAll('[data-gro-article-root]').forEach(initialiseFallbackImages);
    initialiseShareButtons(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-gro-article-root]').forEach((section) => {
      section.groArticleController?.abort();
    });
  });
})();
